<template>
  <section>
    <template v-if="!refundable">
      <div class="font-heading font-semibold text-xl mb-2">Refund</div>
      <div class="font-sans text-base">
        {{ guestInfo.first_name }} {{ guestInfo.last_name }} doesn't have any transactions to refund
        or already has been issued a refund.
      </div>
      <div class="mt-4 mb-2 flex justify-between">
        <ButtonDefault
          id="non-refundable-close-btn"
          label="close"
          class="mr-4"
          @click="emitClose"
        />
      </div>
    </template>
    <template v-if="refundable && showSummary">
      <div class="font-heading font-semibold text-xl mb-2">Refund</div>
      <ErrorDisplay v-bind="ui.error" class="p-2" />
      <div class="flex flex-col my-4">
        <div class="flex justify-between">
          <div class="font-sans text-base">Tickets</div>
          <div class="font-sans text-base">
            <span class="text-fg-secondary"
              >( {{ reservation.number_of_guests }} @
              {{ currency(Number(reservation.amount) / Number(reservation.number_of_guests)) }}
              )</span
            >
            ${{ reservation.amount }}
          </div>
        </div>
        <div class="flex justify-between">
          <div class="font-sans text-base">Service Fee</div>
          <div class="font-sans text-base">{{ currency(reservation.service_fee) }}</div>
        </div>
        <div class="flex justify-between">
          <div class="font-sans text-base">Tax ({{ taxRate }}%)</div>
          <div class="font-sans text-base">{{ currency(reservation.tax) }}</div>
        </div>
        <div class="flex justify-between">
          <div class="font-sans text-base">Total</div>
          <div class="font-sans text-base">
            {{
              currency(
                Number(reservation.tax) +
                  Number(reservation.amount) +
                  Number(reservation.service_fee),
              )
            }}
          </div>
        </div>

        <div class="mt-4">
          <div class="font-label text-xs text-fg-secondary uppercase">Refund Amount</div>
          <div class="flex items-center">
            <span
              class="bg-surface-base py-2 px-2.5 border border-solid border-linear-subtle rounded-tl rounded-bl text-base"
              >$</span
            >
            <input
              type="number"
              id="refund-input-amount"
              class="p-2 border border-solid border-linear-subtle rounded-tr rounded-br text-base bg-surface-primary"
              v-model="form.refundAmount"
            />
          </div>
        </div>
      </div>
      <div class="mb-2 flex justify-between">
        <ButtonDefault
          id="refund-cancel"
          label="close"
          class="w-full mr-4"
          @click="emitClose"
          alt
        />
        <ButtonDefault
          id="refund-confirm"
          class="w-full"
          label="Next"
          @click="state = 'CONFIRMING'"
        />
      </div>
    </template>
    <template v-if="refundable && showConfirmation">
      <div class="font-heading font-semibold text-xl mb-2">Please Confirm</div>
      <div class="mb-16">Are you want to issue a refund of {{ currency(form.refundAmount) }} ?</div>
      <div class="mb-2 flex justify-between">
        <ButtonDefault
          id="refund-back-confirm"
          label="back"
          class="w-full mr-4"
          @click="state = 'DISPLAYING'"
          alt
        />
        <ButtonDefault id="refund-process" class="w-full" label="Refund" @click="onRefund" />
      </div>
    </template>
    <template v-if="refundable && showProcessing">
      <div class="font-heading font-semibold text-xl mb-2">Processing Refund</div>
      <Spinner display />
    </template>
    <template v-if="refundable && showSuccessful">
      <div class="font-heading font-semibold text-xl mb-2">Success</div>
      <div class="mb-16">
        {{ guestInfo.first_name }} {{ guestInfo.last_name }} was refunded ${{ form.refundAmount }}.
        Please note that it could take several seconds to be reflected on the Reservation.
      </div>
      <div class="mb-2 flex justify-center">
        <ButtonDefault id="refund-done" class="px-8" label="Done" @click="emitClose()" />
      </div>
    </template>
  </section>
</template>
<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import Spinner from '@/common/components/atoms/Spinner.vue'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { blankError, clearError, displayHandled } from '@/utils/errors'

import { GuestRead, ReservationDetailedRead, TransactionDetailedRead } from '@generated/types'

export default defineComponent({
  name: 'RefundForm',
  components: {
    ButtonDefault,
    ErrorDisplay,
    Spinner,
  },
  props: {
    reservation: { type: Object as PropType<ReservationDetailedRead>, required: true },
    isModal: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      form: {
        refundAmount: 0 as number,
      },
      state: 'DISPLAYING' as string,
      ui: {
        error: blankError(),
      },
    }
  },
  mounted() {
    this.form.refundAmount = this.maxRefundAmount
  },
  computed: {
    ...mapStores(useReservationStore, useSettingStore),
    ...mapState(useSettingStore, ['currency']),

    paidTransaction(): TransactionDetailedRead | undefined {
      return this.reservation.transactions.find(transaction => transaction.payment_intent_id)
    },

    taxRate(): string {
      return this.settingStore.taxRateString(this.reservation)
    },

    refundable(): boolean {
      if (!this.paidTransaction) return false
      if (this.maxRefundAmount <= Number(this.paidTransaction.refunded)) return false
      return true
    },

    maxRefundAmount(): number {
      if (!this.paidTransaction) return 0
      return (
        Number(this.paidTransaction.tax) +
        Number(this.paidTransaction.amount) +
        Number(this.paidTransaction.service_fee)
      )
    },

    maxRefundString(): string {
      return this.currency(this.maxRefundAmount)
    },

    guestInfo(): GuestRead {
      if (!this.reservation.contact) {
        throw new Error('No guest info found')
      }
      return this.reservation.contact
    },

    showSummary(): boolean {
      return this.state == 'DISPLAYING'
    },

    showConfirmation(): boolean {
      return this.state == 'CONFIRMING'
    },

    showProcessing(): boolean {
      return this.state == 'PROCESSING'
    },

    showSuccessful(): boolean {
      return this.state == 'SUCCESSFUL'
    },
  },
  methods: {
    async onRefund() {
      if (!this.paidTransaction || !this.refundable) return
      const amount = Number(this.form.refundAmount)
      this.state = 'PROCESSING'

      clearError(this.ui.error)
      if (amount > this.maxRefundAmount) {
        this.state = 'DISPLAYING'
        this.ui.error = {
          code: 'client_error',
          message: `Please request a refund amount that is less or equal to ${this.maxRefundString}.`,
        }
        return
      }

      try {
        await this.reservationStore.refundTransaction(Number(this.paidTransaction.id), amount)
        this.state = 'SUCCESSFUL'
      } catch (error) {
        this.state = 'DISPLAYING'
        displayHandled(error, this.ui.error)
      }
    },
    emitClose() {
      this.state = 'DISPLAYING'
      this.$emit('close')
    },
  },
})
</script>
